import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface CustomTemplate {
  id: string;
  user_id: string;
  name: string;
  prompt: string;
  category: string;
  subcategory?: string;
  is_favorite: boolean;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

export const useRevisionTemplates = () => {
  const queryClient = useQueryClient();

  // Fetch custom templates
  const { data: customTemplates = [], isLoading } = useQuery({
    queryKey: ['revision-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_revision_templates')
        .select('*')
        .order('is_favorite', { ascending: false })
        .order('usage_count', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as CustomTemplate[];
    },
  });

  // Create template
  const createTemplate = useMutation({
    mutationFn: async (template: Omit<CustomTemplate, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'usage_count'>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('user_revision_templates')
        .insert({
          user_id: user.id,
          name: template.name,
          prompt: template.prompt,
          category: template.category,
          subcategory: template.subcategory,
          is_favorite: template.is_favorite,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revision-templates'] });
      toast.success('Template created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create template: ' + error.message);
    },
  });

  // Update template
  const updateTemplate = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<CustomTemplate> }) => {
      const { data, error } = await supabase
        .from('user_revision_templates')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revision-templates'] });
      toast.success('Template updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update template: ' + error.message);
    },
  });

  // Delete template
  const deleteTemplate = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('user_revision_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revision-templates'] });
      toast.success('Template deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete template: ' + error.message);
    },
  });

  // Toggle favorite
  const toggleFavorite = useMutation({
    mutationFn: async (id: string) => {
      const template = customTemplates.find(t => t.id === id);
      if (!template) throw new Error('Template not found');

      const { error } = await supabase
        .from('user_revision_templates')
        .update({
          is_favorite: !template.is_favorite,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revision-templates'] });
    },
    onError: (error) => {
      toast.error('Failed to toggle favorite: ' + error.message);
    },
  });

  // Increment usage count
  const incrementUsage = useMutation({
    mutationFn: async (id: string) => {
      const template = customTemplates.find(t => t.id === id);
      if (!template) return;

      const { error } = await supabase
        .from('user_revision_templates')
        .update({
          usage_count: template.usage_count + 1,
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revision-templates'] });
    },
  });

  return {
    customTemplates,
    isLoading,
    createTemplate: createTemplate.mutate,
    updateTemplate: updateTemplate.mutate,
    deleteTemplate: deleteTemplate.mutate,
    toggleFavorite: toggleFavorite.mutate,
    incrementUsage: incrementUsage.mutate,
  };
};
